import React, { useState, createContext, useEffect, useRef, useContext } from 'react';
import { Select } from 'antd';
import { useLanguages } from '../hooks/useMenuQuery';
import { useQueryClient } from '@tanstack/react-query';
import { API } from '../utils/API';
import store from 'store';

const { Option } = Select;

const LayoutContext = createContext();

function LayoutProvider({ children }) {
  const [sideBarCollapsed, setCollapsed] = useState(true); // Start collapsed by default
  const [theme, setTheme] = useState('dark');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successNotification, setSuccessNotification] = useState(false);
  const [headerComponent, setHeaderComponent] = useState(null);
  const [usernameComponent, setUsernameComponent] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null); // Default to null, will be set to Arabic
  const isInitialMount = useRef(true);

  // Check if user is authenticated
  const authToken = store.get('authenticationToken');

  // Fetch languages only if authenticated
  const { data: languagesData, isLoading: isLanguagesLoading, error: languagesError } = useLanguages(authToken);

  // Manual fetch of languages on mount to ensure they load (only if authenticated)
  useEffect(() => {
    if (!authToken) return; // Don't fetch if not authenticated

    const fetchLanguages = async () => {
      try {
        console.log('Manually fetching languages...');
        const response = await API.get('/languages');
        console.log('Manual languages fetch successful:', response.data);
      } catch (error) {
        console.error('Manual languages fetch failed:', error);
      }
    };

    fetchLanguages();
  }, [authToken]);

  // Debug logging
  console.log('Languages data:', languagesData);
  console.log('Languages loading:', isLanguagesLoading);
  console.log('Languages error:', languagesError);

  // Initialize mobile state and handle viewport changes
  useEffect(() => {
    const handleResize = () => {
      const mobileViewport = window.innerWidth <= 768;
      setIsMobile(mobileViewport);

      // On initial load, if we're on desktop, open the sidebar
      if (isInitialMount.current && !mobileViewport) {
        setCollapsed(false);
        isInitialMount.current = false;
      } else if (isInitialMount.current) {
        // If we're on mobile during initial load, keep it closed
        isInitialMount.current = false;
      }
    };

    handleResize(); // Check on initial load
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set default language to Arabic
  useEffect(() => {
    if (languagesData && !selectedLanguage) {
      // Default to Arabic (usually the second language, index 1)
      const arabicLanguage = languagesData.find(lang => lang.name.toLowerCase().includes('arabic')) ||
                            languagesData[1] ||
                            languagesData[0];
      if (arabicLanguage) {
        setSelectedLanguage(arabicLanguage.id);
      }
    }
  }, [languagesData, selectedLanguage]);

  const initialState = {
    sideBarCollapsed,
    setCollapsed,
    theme,
    setTheme,
    loading,
    setLoading,
    error,
    setError,
    successNotification,
    setSuccessNotification,
    setHeaderComponent,
    headerComponent,
    usernameComponent,
    setUsernameComponent,
    isMobile,
    setIsMobile,
    // Language state
    selectedLanguage,
    setSelectedLanguage,
    languages: languagesData || [],
  };

  return <LayoutContext.Provider value={initialState}>{children}</LayoutContext.Provider>;
}

// Language Switcher Component
function LanguageSwitcher({ size = "default", style = {} }) {
  const { selectedLanguage, setSelectedLanguage, languages } = useContext(LayoutContext);

  console.log('LanguageSwitcher render - languages:', languages, 'selectedLanguage:', selectedLanguage);

  // Show loading state or disabled state when languages are not loaded yet
  if (!languages || languages.length === 0) {
    return (
      <Select
        size={size}
        style={{ minWidth: 120, ...style }}
        placeholder="Loading languages..."
        disabled
      />
    );
  }

  return (
    <Select
      value={selectedLanguage || undefined}
      onChange={(value) => {
        console.log('Language changed to:', value);
        setSelectedLanguage(value);
      }}
      size={size}
      style={{ minWidth: 120, ...style }}
      placeholder="Select Language"
    >
      {languages.map(language => (
        <Option value={language.id} key={language.id}>
          {language.name}
        </Option>
      ))}
    </Select>
  );
}

export { LayoutContext, LayoutProvider, LanguageSwitcher };
